import { Building2, ClipboardList, MapPin, UserRound } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { InsuranceInfoSection } from '@/components/claims-filing/common/InsuranceInfoSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContactHandlesList } from '@/components/ui/ContactHandlesList';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetCoverageQuery, useUpdateCoverageMutation } from '@/services/api/coverageService';
import { usePropertiesWithOwnersQuery } from '@/services/api/subjectsService';
import {
  AdjusterDetail,
  BenefitType,
  ClaimDetail,
  ContactHandleSchema,
  CoverageCategory,
} from '@/services/types/coverage-types';

const BENEFIT_TYPE_OPTIONS: Array<{ label: string; value: BenefitType }> = [
  {
    label: 'Bodily Injury Liability',
    value: BenefitType.BODILY_INJURY_LIABILITY,
  },
  {
    label: 'Property Damage Liability',
    value: BenefitType.PROPERTY_DAMAGE_LIABILITY,
  },
  {
    label: 'Personal Injury Protection (PIP)',
    value: BenefitType.PERSONAL_INJURY_PROTECTION,
  },
  {
    label: 'Medical Payments',
    value: BenefitType.MEDICAL_PAYMENTS,
  },
  {
    label: 'Workers Compensation',
    value: BenefitType.WORKERS_COMPENSATION,
  },
  {
    label: 'Other',
    value: BenefitType.OTHER,
  },
];

const formatPropertyAddress = (
  property:
    | {
        property_name?: string | null;
        physical_address?: {
          line1?: string | null;
          line2?: string | null;
          city?: string | null;
          state?: string | null;
          zip?: string | null;
        } | null;
      }
    | undefined,
): string | null => {
  if (!property) {
    return null;
  }

  const address = property.physical_address;
  if (address) {
    const cityStateZip = [address.city, address.state, address.zip]
      .filter(Boolean)
      .join(', ');
    const parts = [address.line1, address.line2, cityStateZip].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }

  return property.property_name ?? null;
};

export const OpenPremisesClaimPage: React.FC = () => {
  const { matter_id: matterId, coverage_id: coverageId } = useParams<{
    matter_id: string;
    coverage_id: string;
  }>();

  const { data: coverage } = useGetCoverageQuery(
    { matterId: matterId ?? '', coverageId: coverageId ?? '' },
    { skip: !matterId || !coverageId },
  );
  const { data: property } = usePropertiesWithOwnersQuery(
    {
      id: coverage?.property_id ?? '',
      matterId: matterId ?? '',
    },
    {
      skip:
        !matterId ||
        !coverage?.property_id ||
        coverage.category !== CoverageCategory.PROPERTY,
    },
  );
  const [updateCoverage, { isLoading: isUpdatingCoverage }] =
    useUpdateCoverageMutation();

  const primaryClaim = coverage?.claims?.[0] ?? null;
  const primaryAdjuster = coverage?.adjusters?.[0] ?? null;

  const [claimNumber, setClaimNumber] = React.useState(primaryClaim?.claim_identifier ?? '');
  const [benefitType, setBenefitType] = React.useState<BenefitType | null>(
    primaryClaim?.benefit_type ?? primaryAdjuster?.benefit_type ?? null,
  );
  const [adjusterName, setAdjusterName] = React.useState(primaryAdjuster?.name ?? '');
  const [adjusterHandles, setAdjusterHandles] = React.useState<ContactHandleSchema[]>(
    primaryAdjuster?.handles ?? [],
  );

  // Sync form state when coverage data loads asynchronously
  const [synced, setSynced] = React.useState(false);
  React.useEffect(() => {
    if (synced || !coverage) return;
    const claim = coverage.claims?.[0] ?? null;
    const adjuster = coverage.adjusters?.[0] ?? null;
    setClaimNumber(claim?.claim_identifier ?? '');
    setBenefitType(claim?.benefit_type ?? adjuster?.benefit_type ?? null);
    setAdjusterName(adjuster?.name ?? '');
    setAdjusterHandles(adjuster?.handles ?? []);
    setSynced(true);
  }, [coverage, synced]);

  const propertyAddress = React.useMemo(() => {
    return (
      formatPropertyAddress(property) ??
      coverage?.named_insured?.[0] ??
      coverage?.carrier?.name ??
      'Property coverage'
    );
  }, [coverage?.carrier?.name, coverage?.named_insured, property]);

  const saveClaimWorkspace = React.useCallback(async () => {
    if (!matterId || !coverageId || !coverage) {
      return;
    }

    const existingClaims = coverage.claims ?? [];
    const existingAdjusters = coverage.adjusters ?? [];
    const additionalClaims = existingClaims.slice(1);
    const additionalAdjusters = existingAdjusters.slice(1);

    const nextPrimaryClaim: ClaimDetail | null =
      claimNumber.trim() || benefitType
        ? {
            benefit_type: benefitType,
            claim_identifier: claimNumber.trim() || null,
            source: existingClaims[0]?.source ?? null,
          }
        : null;

    const nextPrimaryAdjuster: AdjusterDetail | null =
      adjusterName.trim() || adjusterHandles.length > 0 || benefitType
        ? {
            benefit_type: benefitType,
            name: adjusterName.trim() || null,
            handles: adjusterHandles.length > 0 ? adjusterHandles : null,
            source: existingAdjusters[0]?.source ?? null,
          }
        : null;

    try {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: {
          claims: nextPrimaryClaim
            ? [nextPrimaryClaim, ...additionalClaims]
            : additionalClaims,
          adjusters: nextPrimaryAdjuster
            ? [nextPrimaryAdjuster, ...additionalAdjusters]
            : additionalAdjusters,
        },
      }).unwrap();
      toast.success('Open claim details saved');
    } catch (error) {
      toast.error('Failed to save open claim details');
    }
  }, [
    adjusterHandles,
    adjusterName,
    benefitType,
    claimNumber,
    coverage,
    coverageId,
    matterId,
    updateCoverage,
  ]);

  const handleAdjusterHandlesUpdate = React.useCallback(
    async (handles: ContactHandleSchema[]) => {
      setAdjusterHandles(handles);
      if (!matterId || !coverageId || !coverage) {
        return;
      }

      const existingAdjusters = coverage.adjusters ?? [];
      const additionalAdjusters = existingAdjusters.slice(1);
      const nextPrimaryAdjuster: AdjusterDetail = {
        benefit_type: benefitType,
        name: adjusterName.trim() || null,
        handles: handles.length > 0 ? handles : null,
        source: existingAdjusters[0]?.source ?? null,
      };

      try {
        await updateCoverage({
          matterId,
          coverageId,
          coverage: {
            adjusters: [nextPrimaryAdjuster, ...additionalAdjusters],
          },
        }).unwrap();
      } catch (error) {
        toast.error('Failed to save adjuster contact info');
      }
    },
    [
      adjusterName,
      benefitType,
      coverage,
      coverageId,
      matterId,
      updateCoverage,
    ],
  );

  return (
    <div className="space-y-6 px-4 py-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-orange-600" />
            Property Location
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium text-zinc-900">{propertyAddress}</p>
          <p className="mt-2 text-sm text-zinc-600">
            Use this workspace to find the carrier, call manually, open the
            premises claim, and save the coverage details you confirm.
          </p>
        </CardContent>
      </Card>

      {coverage && (
        <InsuranceInfoSection
          matterId={matterId ?? ''}
          coverage={coverage}
        />
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-violet-600" />
            Claim Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="premises-claim-number">Claim Number</Label>
              <Input
                id="premises-claim-number"
                placeholder="Enter claim number"
                value={claimNumber}
                onChange={(event) => setClaimNumber(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="premises-benefit-type">Benefit Type</Label>
              <Select
                value={benefitType ?? undefined}
                onValueChange={(value) => setBenefitType(value as BenefitType)}
              >
                <SelectTrigger id="premises-benefit-type">
                  <SelectValue placeholder="Select benefit type" />
                </SelectTrigger>
                <SelectContent>
                  {BENEFIT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={saveClaimWorkspace}
              disabled={isUpdatingCoverage}
            >
              {isUpdatingCoverage ? 'Saving...' : 'Save Claim Info'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserRound className="h-4 w-4 text-blue-600" />
            Adjuster Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="premises-adjuster-name">Adjuster Name</Label>
            <Input
              id="premises-adjuster-name"
              placeholder="Enter adjuster name"
              value={adjusterName}
              onChange={(event) => setAdjusterName(event.target.value)}
            />
          </div>

          <ContactHandlesList
            handles={adjusterHandles}
            onUpdate={handleAdjusterHandlesUpdate}
            title="Adjuster Contact Info"
            emptyStateTitle="No adjuster contacts yet"
            emptyStateDescription="Add phone, fax, email, or website details for the adjuster."
          />

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={saveClaimWorkspace}
              disabled={isUpdatingCoverage}
            >
              {isUpdatingCoverage ? 'Saving...' : 'Save Contact Info'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-zinc-700" />
            Manual Claim Opening
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-zinc-600">
          <p>1. Confirm the right carrier and division.</p>
          <p>2. Call the carrier manually using the saved contact information.</p>
          <p>3. Capture the claim number, benefit type, and adjuster details.</p>
          <p>4. Save the coverage info here, then use the top-right action to mark the task complete.</p>
        </CardContent>
      </Card>
    </div>
  );
};
